import { useEffect, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ArbeitstischAufgabe, arbeitstischAufgaben, arbeitstischAufgabenSpeichern, arbeitstischAufgabenZuruecksetzen,
  ArbeitstischGrundanweisung, arbeitstischGrundanweisung, arbeitstischGrundanweisungSpeichern, arbeitstischGrundanweisungZuruecksetzen,
} from '../../api/unimedica';

/** Verwaltung → Arbeitstisch (#3): die Aufgaben-Knöpfe der Praxis. Nur Admins. */
function kennung(name: string, vorhanden: string[]): string {
  const basis = name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'aufgabe';
  let k = basis; let n = 2;
  while (vorhanden.includes(k)) k = `${basis}-${n++}`;
  return k;
}

export default function ArbeitstischTab() {
  const [liste, setListe] = useState<ArbeitstischAufgabe[]>([]);
  const [geaendert, setGeaendert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const [grund, setGrund] = useState<ArbeitstischGrundanweisung | null>(null);
  const [grundText, setGrundText] = useState<string | null>(null);   // null = nicht in Bearbeitung
  const [grundMeldung, setGrundMeldung] = useState<string | null>(null);

  useEffect(() => { arbeitstischAufgaben().then(setListe).catch(() => setMeldung('Laden fehlgeschlagen')); }, []);
  useEffect(() => { arbeitstischGrundanweisung().then(setGrund).catch(() => setGrundMeldung('Laden fehlgeschlagen')); }, []);

  const aendern = (i: number, feld: 'name' | 'anweisung', wert: string) => {
    setListe((l) => l.map((a, j) => (j === i ? { ...a, [feld]: wert } : a))); setGeaendert(true);
  };
  const schieben = (i: number, d: -1 | 1) => {
    setListe((l) => { const n = [...l]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; }); setGeaendert(true);
  };
  const speichern = async () => {
    try {
      const sauber = liste.map((a) => ({ ...a, name: a.name.trim(), anweisung: a.anweisung.trim() }));
      if (sauber.some((a) => !a.name || !a.anweisung)) { setMeldung('Jede Aufgabe braucht Name und Anweisung.'); return; }
      setListe(await arbeitstischAufgabenSpeichern(sauber)); setGeaendert(false); setMeldung('Gespeichert.');
    } catch (e: any) { setMeldung(String(e?.message ?? e)); }
  };
  const zuruecksetzen = async () => {
    setListe(await arbeitstischAufgabenZuruecksetzen()); setGeaendert(false); setMeldung('Vorlagen wiederhergestellt.');
  };

  return (
    <section className="space-y-4">
      <div className="card">
        <h2 className="page-h1 mb-1">Arbeitstisch — Aufgaben</h2>
        <p className="text-xs leading-relaxed text-ps-dark-400">
          Jede Aufgabe erscheint auf dem Arbeitstisch als Knopf. Die Anweisung sagt der KI, was aus der Unterlage werden
          soll — z. B. Stil, Länge, Aufzählung. Die KI hat dort weder Internet noch Akten.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button className="btn-primary disabled:opacity-50" disabled={!geaendert} onClick={() => void speichern()}>Speichern</button>
          <button className="btn-ghost inline-flex items-center gap-1.5" disabled={liste.length >= 20}
            onClick={() => { setListe((l) => [...l, { id: kennung('neue-aufgabe', l.map((a) => a.id)), name: '', anweisung: '' }]); setGeaendert(true); }}>
            <PlusIcon className="h-4 w-4" /> Aufgabe hinzufügen
          </button>
          <button className="btn-ghost" onClick={() => void zuruecksetzen()}>Auf Vorlagen zurücksetzen</button>
          {meldung && <span className="text-xs text-ps-dark-500">{meldung}</span>}
        </div>
      </div>
      <div className="card space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ps-dark-700">Grundanweisung</span>
            {grund && (grund.standard ? <span className="badge badge-neutral">Standard</span> : <span className="badge badge-warn">angepasst</span>)}
          </div>
          <div className="flex items-center gap-2">
            {grundText !== null ? (<>
              <button className="btn-ghost" onClick={() => setGrundText(null)}>Abbrechen</button>
              <button className="btn-primary" onClick={async () => {
                try { setGrund(await arbeitstischGrundanweisungSpeichern(grundText)); setGrundText(null); setGrundMeldung('Gespeichert.'); }
                catch (e: any) { setGrundMeldung(String(e?.message ?? e)); }
              }}>Speichern</button>
            </>) : (<>
              {grund && !grund.standard && <button className="btn-ghost" onClick={async () => { setGrund(await arbeitstischGrundanweisungZuruecksetzen()); setGrundMeldung('Auf Standard zurückgesetzt.'); }}>Auf Standard zurücksetzen</button>}
              <button className="btn-ghost" onClick={() => setGrundText(grund?.text ?? '')}>Bearbeiten</button>
            </>)}
          </div>
        </div>
        <p className="text-xs text-ps-dark-400">Gilt für jede Aufgabe auf dem Arbeitstisch: Stil, Sprache, was die KI nie tun soll. Die Anweisung des Knopfs kommt dazu.</p>
        {grundText !== null
          ? <textarea className="input-field font-mono !text-[12.5px] leading-[1.55]" rows={8} maxLength={8000} value={grundText} onChange={(e) => setGrundText(e.target.value)} />
          : <pre className="whitespace-pre-wrap rounded-md bg-gray-50 px-4 py-3 font-mono text-[12px] leading-[1.6] text-ps-dark-700">{grund?.text ?? '…'}</pre>}
        {grundMeldung && <div className="text-xs text-ps-dark-500">{grundMeldung}</div>}
      </div>
      <h3 className="px-1 pt-2 text-sm font-semibold text-ps-dark-700">Aufgaben-Knöpfe</h3>
      {liste.map((a, i) => (
        <div key={a.id} className="card space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input className="input-field max-w-xs flex-1 text-sm font-semibold" value={a.name} maxLength={60}
              placeholder="Name des Knopfs" onChange={(e) => aendern(i, 'name', e.target.value)} />
            <div className="ml-auto flex items-center gap-1">
              <button className="btn-ghost h-9 w-9 !p-0" aria-label="Nach oben" disabled={i === 0} onClick={() => schieben(i, -1)}><ArrowUpIcon className="mx-auto h-4 w-4" /></button>
              <button className="btn-ghost h-9 w-9 !p-0" aria-label="Nach unten" disabled={i === liste.length - 1} onClick={() => schieben(i, 1)}><ArrowDownIcon className="mx-auto h-4 w-4" /></button>
              <button className="btn-ghost h-9 w-9 !p-0 text-red-600" aria-label="Löschen" disabled={liste.length <= 1}
                onClick={() => { setListe((l) => l.filter((_, j) => j !== i)); setGeaendert(true); }}><TrashIcon className="mx-auto h-4 w-4" /></button>
            </div>
          </div>
          <textarea className="input-field text-[13px] leading-[1.55]" rows={4} value={a.anweisung} maxLength={4000}
            placeholder="Anweisung an die KI" onChange={(e) => aendern(i, 'anweisung', e.target.value)} />
        </div>
      ))}
    </section>
  );
}
